import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Clock, CurrencyDollar, ArrowRight, DotsThreeOutline } from '@phosphor-icons/react';
import SaveJobButton from './SaveJobButton';
import JobQuickActionsSheet from './JobQuickActionsSheet';
import { haptic } from '../utils/haptics';

const SimpleJobCard = ({ job, showMatchScore = false, matchScore = null }) => {
  const navigate = useNavigate();
  const [sheetOpen, setSheetOpen] = useState(false);

  const formatBudget = (min, max) => {
    if (max && max > 0) {
      return `$${max.toLocaleString()}`;
    }
    if (min && min > 0) {
      return `$${min.toLocaleString()}+`;
    }
    return 'Negotiable';
  };

  return (
    <div 
      className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-600 rounded-xl p-5 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F] hover:shadow-[6px_6px_0px_#000] dark:hover:shadow-[6px_6px_0px_#2B4C6F] transition-all"
      data-testid={`job-card-${job._id}`}
    >
      {/* Subcontractor Badge */}
      {['subcontractors', 'both'].includes(job.open_to) && (
        <div className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold mb-3 mr-2 bg-orange-100 dark:bg-orange-900/50 text-orange-800 dark:text-orange-300 border-2 border-[#EA580C]"
          data-testid="subcontractor-badge">
          {job.open_to === 'both' ? 'Workers & Subs' : 'Subcontractors'}
        </div>
      )}

      {/* Match Score Badge */}
      {showMatchScore && matchScore && (
        <div className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-bold mb-3 ${
          matchScore >= 80 ? 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300 border-2 border-green-600' :
          matchScore >= 60 ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300 border-2 border-blue-600' :
          'bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-300 border-2 border-amber-600'
        }`}>
          {Math.round(matchScore)}% Match
        </div>
      )}

      {/* Pay Rate - Most Important */}
      <div className="flex items-center gap-2 mb-2">
        <CurrencyDollar size={28} weight="bold" className="text-green-600 dark:text-green-400" />
        <span className="text-3xl font-black text-green-600 dark:text-green-400">
          {formatBudget(job.budget_min, job.budget_max)}
        </span>
      </div>

      {/* Job Title */}
      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3 line-clamp-2">
        {job.featured && (
          <span className="inline-flex items-center gap-1 align-middle bg-amber-400 text-amber-950 text-[10px] font-black uppercase px-2 py-0.5 rounded border border-amber-600 mr-2" data-testid="featured-job-badge">
            ★ Featured
          </span>
        )}
        {job.title}
      </h3>

      {/* Quick Info */}
      <div className="flex flex-wrap gap-3 mb-4">
        {job.location && (
          <div className="flex items-center gap-1 text-gray-700 dark:text-gray-300">
            <MapPin size={20} weight="bold" />
            <span className="font-medium">{job.location}</span>
          </div>
        )}
        {job.duration && (
          <div className="flex items-center gap-1 text-gray-700 dark:text-gray-300">
            <Clock size={20} weight="bold" />
            <span className="font-medium">{job.duration}</span>
          </div>
        )}
      </div>

      {/* Skills - Simple Tags */}
      {job.required_skills?.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {job.required_skills.slice(0, 3).map((skill, idx) => (
            <span 
              key={idx}
              className="px-3 py-1 bg-gray-100 dark:bg-neutral-700 border-2 border-gray-300 dark:border-neutral-500 rounded-full text-sm font-bold text-gray-700 dark:text-gray-200"
            >
              {skill}
            </span>
          ))}
          {job.required_skills.length > 3 && (
            <span className="px-3 py-1 bg-gray-100 dark:bg-neutral-700 border-2 border-gray-300 dark:border-neutral-500 rounded-full text-sm font-bold text-gray-500 dark:text-gray-400">
              +{job.required_skills.length - 3} more
            </span>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-3 mt-4">
        <button
          onClick={() => navigate(`/job/${job._id}`)}
          className="flex-1 flex items-center justify-center gap-2 h-14 bg-[#2B4C6F] text-white font-bold text-lg rounded-xl border-2 border-black dark:border-neutral-600 shadow-[3px_3px_0px_#000] dark:shadow-[3px_3px_0px_#2B4C6F] hover:shadow-[4px_4px_0px_#000] dark:hover:shadow-[4px_4px_0px_#2B4C6F] active:translate-y-1 active:shadow-none transition-all"
          data-testid={`view-job-${job._id}`}
        >
          View Job
          <ArrowRight size={24} weight="bold" />
        </button>
        <SaveJobButton 
          jobId={job._id} 
          size="lg"
          className="h-14 w-14 border-2 border-black dark:border-neutral-600 shadow-[3px_3px_0px_#000] dark:shadow-[3px_3px_0px_#2B4C6F]"
        />
        <button
          onClick={() => { haptic(8); setSheetOpen(true); }}
          className="h-14 w-14 flex items-center justify-center bg-white dark:bg-neutral-700 text-neutral-700 dark:text-neutral-200 rounded-xl border-2 border-black dark:border-neutral-600 shadow-[3px_3px_0px_#000] dark:shadow-[3px_3px_0px_#2B4C6F] active:translate-y-1 active:shadow-none transition-all"
          aria-label="More actions"
          data-testid={`job-actions-${job._id}`}
        >
          <DotsThreeOutline size={22} weight="fill" />
        </button>
      </div>

      <JobQuickActionsSheet job={job} open={sheetOpen} onOpenChange={setSheetOpen} />
    </div>
  );
};

export default SimpleJobCard;
